// The at-a-glance OTA status: is the gate live, what's being served, the native
// floor / apply-mode / last-release, and a real "Verify live bundle" health check
// that fetches the bundle players would download and re-checks its sha256.
import type { ReactNode } from 'react';
import { CheckCircle2, RefreshCw, ShieldCheck, XCircle } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { timeAgo } from '../parts';
import { formatBytes } from './fields';
import type { OtaAdmin } from './useOtaAdmin';

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-black/5 bg-wash px-3 py-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink3">{label}</div>
      <div className="mt-0.5 text-[13px] font-bold text-ink">{value}</div>
    </div>
  );
}

function CheckRow({ ok, children }: { ok: boolean | null; children: ReactNode }) {
  const tone = ok === true ? 'text-accent-ink' : ok === false ? 'text-danger' : 'text-ink2';
  const Icon = ok === false ? XCircle : CheckCircle2;
  return (
    <div className={cn('flex items-center gap-1.5 text-[12px] font-semibold', tone)}>
      {ok !== null && <Icon className="h-3.5 w-3.5 shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

export function OtaStatusCard({ admin }: { admin: OtaAdmin }) {
  const { stored, verify, verifyState, verifyResult, verifyError } = admin;
  const serving = !!stored?.enabled && !!stored?.version;

  return (
    <div className="rounded-2xl border border-black/5 bg-card p-4 shadow-tile">
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl px-3.5 py-3',
          serving ? 'bg-accent-wash' : 'bg-fill'
        )}
      >
        <span
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            serving ? 'bg-accent text-white' : 'bg-black/10 text-ink2'
          )}
        >
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className={cn('text-[14px] font-extrabold', serving ? 'text-accent-ink' : 'text-ink')}>
            {serving ? `Serving v${stored?.version}` : 'Live updates paused'}
          </div>
          <div className="text-[12px] text-ink2">
            {serving
              ? 'Native apps pull this bundle on next open (or within seconds if open).'
              : 'The kill switch is off — no install updates, whatever the pointer says.'}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat label="Live version" value={stored?.version ? `v${stored.version}` : '—'} />
        <Stat
          label="Native floor"
          value={stored?.minNativeBuild != null ? `build ${stored.minNativeBuild}+` : 'any build'}
        />
        <Stat label="Apply mode" value={stored?.mandatory ? 'Immediately' : 'On next open'} />
        <Stat label="Last release" value={stored?.updatedAt ? timeAgo(stored.updatedAt) : '—'} />
      </div>

      <div className="mt-3 border-t border-black/5 pt-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[13px] font-bold text-ink">Live bundle health</div>
            <div className="text-[11px] text-ink3">
              Fetches the live bundle server-side and re-checks its checksum.
            </div>
          </div>
          <button
            type="button"
            onClick={() => verify()}
            disabled={verifyState === 'checking' || !stored?.version}
            className={cn(
              'tap inline-flex items-center gap-1.5 rounded-xl border border-black/10 bg-wash px-3 py-2 text-[13px] font-semibold text-ink',
              (verifyState === 'checking' || !stored?.version) && 'opacity-50'
            )}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', verifyState === 'checking' && 'animate-spin')} />
            {verifyState === 'checking' ? 'Checking…' : 'Verify live bundle'}
          </button>
        </div>

        {verifyState === 'error' && verifyError && (
          <p className="mt-2 text-[12px] font-semibold text-danger">{verifyError}</p>
        )}
        {verifyResult && (
          <div className="mt-2.5 rounded-xl border border-black/5 bg-wash px-3 py-2.5">
            {verifyResult.error ? (
              <CheckRow ok={false}>
                Couldn&apos;t verify
                {verifyResult.version ? ` v${verifyResult.version}` : ''}: {verifyResult.error}
              </CheckRow>
            ) : (
              <div className="grid gap-1.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink3">
                  v{verifyResult.version} · {formatBytes(verifyResult.sizeBytes)}
                </div>
                <CheckRow ok={verifyResult.reachable}>
                  {verifyResult.reachable
                    ? `Reachable (HTTP ${verifyResult.httpStatus ?? 200})`
                    : 'Unreachable'}
                </CheckRow>
                <CheckRow ok={verifyResult.checksumMatch}>
                  {verifyResult.checksumMatch === true
                    ? 'Checksum matches the manifest'
                    : verifyResult.checksumMatch === false
                      ? 'Checksum MISMATCH — do not enable'
                      : 'No stored checksum to compare'}
                </CheckRow>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
